import { useTheme } from 'foundations-theming/utils';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import useDebouncedEditorStateContext from 'hubspot-prosemirror/prosemirror/contexts/useDebouncedEditorStateContext';
import useContentSearch from 'hubspot-prosemirror/prosemirror/utils/useContentSearch';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { List } from 'foundations-components/transitional/components/List';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Flex } from 'foundations-components/components/Flex';
import PersonalizationTokenInlineFallback from './PersonalizationTokenInlineFallback';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function groupTokens(tokens) {
  const groupMap = new Map();
  for (const token of tokens) {
    const group = token.groupName || '';
    const existing = groupMap.get(group);
    if (existing) {
      existing.push(token);
    } else {
      groupMap.set(group, [token]);
    }
  }
  const groups = [];
  for (const [groupName, groupTokensList] of groupMap) {
    groups.push({
      groupName,
      tokens: groupTokensList
    });
  }
  return groups;
}
export default function PersonalizationTokenInlineDropdown({
  pluginKey,
  fetchTokens,
  onSelect,
  onTokensChange,
  onConfirmToken,
  onCancelPendingToken,
  defaultValueConfig
}) {
  const theme = useTheme();
  const {
    editorState
  } = useDebouncedEditorStateContext();
  const pluginState = pluginKey.getState(editorState);
  const {
    searchText,
    selectedIndex,
    pendingToken
  } = pluginState;
  const [isPendingSearch, setIsPendingSearch] = useState(true);
  const trackedFetch = useMemo(() => {
    return args => {
      return fetchTokens(args).finally(() => {
        setIsPendingSearch(false);
      });
    };
  }, [fetchTokens]);
  const [,, setSearchQuery, tokens, isLoading] = useContentSearch(trackedFetch, undefined, undefined, searchText);
  const tokenList = useMemo(() => tokens || [], [tokens]);
  useEffect(() => {
    onTokensChange(tokenList);
  }, [tokenList, onTokensChange]);
  const trimmedSearchText = searchText.trim();
  useEffect(() => {
    setIsPendingSearch(true);
    setSearchQuery(trimmedSearchText);
  }, [trimmedSearchText, setSearchQuery]);
  const selectedRef = useRef(null);
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({
        block: 'nearest'
      });
    }
  }, [selectedIndex, tokenList]);
  const clampedIndex = tokenList.length === 0 ? -1 : Math.min(selectedIndex, tokenList.length - 1);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  useEffect(() => {
    setHoveredIndex(null);
  }, [selectedIndex, tokenList]);
  const activeIndex = hoveredIndex !== null ? hoveredIndex : clampedIndex;
  const handleMouseEnter = useCallback(index => {
    setHoveredIndex(index);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);
  const groups = useMemo(() => groupTokens(tokenList), [tokenList]);
  const selectedStyle = {
    backgroundColor: theme.color['fill-field-hover']
  };
  const dropdownContent = /*#__PURE__*/_jsx(List, {
    styled: false,
    maxBlockSize: "300px",
    minInlineSize: "240px",
    overflowX: "hidden",
    overflowY: "auto",
    position: "relative",
    margin: "0",
    padding: "0",
    "data-test-id": "personalization-token-inline-dropdown-list",
    children: (isLoading || isPendingSearch) && tokenList.length === 0 ? /*#__PURE__*/_jsx("li", {
      children: /*#__PURE__*/_jsx(Flex, {
        minBlockSize: "50px",
        justifyContent: "center",
        alignItems: "center",
        children: /*#__PURE__*/_jsx(LoadingSpinner, {
          size: "sm"
        })
      })
    }) : tokenList.length === 0 ? /*#__PURE__*/_jsx("li", {
      style: {
        padding: `${theme.space['300']}`,
        fontSize: '14px'
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.personalizationToken.inlineDropdown.noResults"
      })
    }) : groups.map(group => {
      const groupItems = group.tokens.map(token => {
        const flatIndex = tokenList.findIndex(t => t.tokenKey === token.tokenKey);
        const isActive = flatIndex === activeIndex;
        return /*#__PURE__*/_jsx("li", {
          style: Object.assign({
            padding: `${theme.space['200']} ${theme.space['300']} ${theme.space['200']} ${theme.space['600']}`,
            cursor: 'pointer',
            fontSize: '14px'
          }, isActive ? selectedStyle : undefined),
          ref: flatIndex === clampedIndex ? selectedRef : undefined,
          onMouseEnter: () => handleMouseEnter(flatIndex),
          onMouseLeave: handleMouseLeave,
          onMouseDown: e => {
            e.preventDefault();
            onSelect(token);
          },
          "data-test-id": "personalization-token-inline-option",
          children: token.displayText || token.tokenKey
        }, token.tokenKey);
      });
      if (!group.groupName) {
        return /*#__PURE__*/_jsx(Fragment, {
          children: groupItems
        }, "ungrouped");
      }
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx("li", {
          style: {
            padding: `${theme.space['300']} ${theme.space['400']}`,
            fontWeight: 600,
            fontSize: '14px',
            cursor: 'default'
          },
          "data-test-id": "personalization-token-inline-group-header",
          children: group.groupName
        }), groupItems]
      }, `group-${group.groupName}`);
    })
  });
  const popoverContent = pendingToken ? /*#__PURE__*/_jsx(PersonalizationTokenInlineFallback, {
    token: pendingToken,
    isRequired: defaultValueConfig === null || defaultValueConfig === void 0 ? void 0 : defaultValueConfig.required,
    onInsert: onConfirmToken,
    onCancel: onCancelPendingToken
  }) : dropdownContent;
  return /*#__PURE__*/_jsx(Popover, {
    autoPlacement: true,
    arrowSize: "none",
    content: popoverContent,
    open: true,
    placement: "bottom right",
    "data-test-id": "personalization-token-inline-popover",
    children: /*#__PURE__*/_jsx("span", {})
  });
}